import { ReactElement, memo, useMemo, useCallback } from 'react';

import {
  useProtocolItemStatus,
  withProtocolItemStatus,
} from 'components/common/withProtocolItemStatus';
import { MemoChecklistItemPreview } from 'components/template/ChecklistItemPreview';
import { ProtocolItemAnswersForm } from '../ProtocolItemAnswersForm';
import {
  MemoProtocolItemResultIcon,
  ProtocolItemErrorIcon,
} from '../ComplianceCheckResult/Icon';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { ChecklistItemOnView } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { findError } from './model';
import { useInspection } from 'store/inspection';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { isProtocolAnswerFilled } from 'shared/domain/protocolAnswer';
import { findProtocolAnswerByChecklistAnswerId } from 'shared/domain/protocolAnswer/findProtocolAnswerByChecklistAnswerId';
import { isChecklistItemFullyNotApplicable } from 'shared/domain/inspection/notApplicableUi';
import { shouldShowComplianceStatus } from 'shared/domain/template/checklistAnswerGuards';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { createInspectionChecklistItemRoute } from 'views/inspections/single/helpers';
import { useProtocolOnViewForChecklistItem } from 'components/inspection/protocolLookup';

interface ProtocolItemProps {
  checklistItem: ChecklistItemOnView;
  index: number;
  displayNumber?: string;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  isExpandingAll?: boolean;
  allExpanded?: boolean;
}

export interface InspectionProtocolItemRowProps {
  checklistItem: ChecklistItemOnView;
  index: number;
  displayNumber?: string;
  isExpanded: boolean;
  onItemExpandedChange: (id: string, expanded: boolean) => void;
  isExpandingAll: boolean;
  allExpanded: boolean;
}

function ProtocolItem({
  checklistItem,
  index,
  displayNumber,
  expanded,
  onExpandedChange,
  isExpandingAll,
  allExpanded,
}: ProtocolItemProps): ReactElement {
  const { status: contextStatus } = useProtocolItemStatus();
  const { errors, values } = useInputForm();
  const { inspection } = useInspection();
  const projectId = useAppUrlParams(UrlParamName.PROJECT_ID);
  const protocolItem = useProtocolOnViewForChecklistItem(checklistItem._id);

  const error = findError(errors, checklistItem._id);
  const status: ComplianceAnswerOption =
    protocolItem?.complianceCheck.result ?? contextStatus;

  const CheckIcon = useMemo(() => {
    return error
      ? (): ReactElement => <ProtocolItemErrorIcon />
      : (): ReactElement => <MemoProtocolItemResultIcon status={status} />;
  }, [status, error]);

  const isAnswerFilledInForm = useCallback(
    (answerId: string, answerType: AnswerType): boolean => {
      return isProtocolAnswerFilled(
        values,
        checklistItem._id,
        answerId,
        answerType
      );
    },
    [checklistItem._id, values]
  );

  const answerProgress = useMemo(() => {
    const answerConfigs = (checklistItem.answers ?? []).filter(
      (answer) => !answer.deleted
    );
    const totalAnswers = answerConfigs.length;
    if (totalAnswers === 0) return undefined;

    const isNotApplicable = isChecklistItemFullyNotApplicable(
      answerConfigs.map((answer) => answer._id),
      (answerId) =>
        findProtocolAnswerByChecklistAnswerId(protocolItem?.answers, answerId)
          ?.isApplicable !== false
    );

    const answeredCount = answerConfigs.filter((answer) =>
      isAnswerFilledInForm(answer._id, answer.type)
    ).length;

    return { answered: answeredCount, total: totalAnswers, isNotApplicable };
  }, [checklistItem.answers, isAnswerFilledInForm, protocolItem?.answers]);

  const showComplianceStatusIcon = useMemo(() => {
    if (answerProgress?.isNotApplicable) {
      return false;
    }

    return shouldShowComplianceStatus(checklistItem.answers, status);
  }, [answerProgress?.isNotApplicable, checklistItem.answers, status]);

  const internalEntityBaseUrlOverride = useCallback((): string => {
    return createInspectionChecklistItemRoute(
      projectId,
      inspection?._id ?? '',
      checklistItem._id
    );
  }, [projectId, inspection?._id, checklistItem._id]);

  return (
    <MemoChecklistItemPreview
      key={checklistItem._id}
      checklistItem={checklistItem}
      index={index}
      displayNumber={displayNumber}
      CheckIcon={showComplianceStatusIcon ? CheckIcon : undefined}
      error={error}
      inspectionId={inspection?._id}
      internalEntityBaseUrlOverride={
        inspection?._id ? internalEntityBaseUrlOverride : undefined
      }
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      isExpandingAll={isExpandingAll}
      hideAnswersPreview
      answerProgress={answerProgress}
      showStatusIcon={showComplianceStatusIcon}
    >
      <ProtocolItemAnswersForm
        checklistItemId={checklistItem._id}
        allExpanded={allExpanded}
        answerConfigs={checklistItem.answers}
      />
    </MemoChecklistItemPreview>
  );
}

//@ts-ignore
export const MemoProtocolItem = withProtocolItemStatus(memo(ProtocolItem));

function InspectionProtocolItemRow({
  checklistItem,
  index,
  displayNumber,
  isExpanded,
  onItemExpandedChange,
  isExpandingAll,
  allExpanded,
}: InspectionProtocolItemRowProps): ReactElement {
  const handleChange = useCallback(
    (expanded: boolean) => {
      onItemExpandedChange(checklistItem._id, expanded);
    },
    [checklistItem._id, onItemExpandedChange]
  );

  return (
    <MemoProtocolItem
      checklistItem={checklistItem}
      index={index}
      displayNumber={displayNumber}
      expanded={isExpanded}
      onExpandedChange={handleChange}
      isExpandingAll={isExpandingAll}
      allExpanded={allExpanded}
    />
  );
}

export const MemoInspectionProtocolItemRow = memo(InspectionProtocolItemRow);
